import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'

/** Billable events metered to the wallet. Mirrors the backend
 * `UsageEventType` text choices. */
export const usageEventTypeEnum = {
  LLM_INPUT_TOKENS: 'llm_input_tokens',
  LLM_OUTPUT_TOKENS: 'llm_output_tokens',
  WHATSAPP_MESSAGE: 'whatsapp_message',
  EMBEDDING_TOKENS: 'embedding_tokens',
} as const

export type UsageEventTypeValue = (typeof usageEventTypeEnum)[keyof typeof usageEventTypeEnum]

/** Display label per event type. LLM input/output are shown together as
 * "LLM tokens" in the breakdown, so both map to the same label. */
export const usageEventTypeLabelMap: Record<UsageEventTypeValue, string> = {
  [usageEventTypeEnum.LLM_INPUT_TOKENS]: 'LLM tokens',
  [usageEventTypeEnum.LLM_OUTPUT_TOKENS]: 'LLM tokens',
  [usageEventTypeEnum.WHATSAPP_MESSAGE]: 'WhatsApp messages',
  [usageEventTypeEnum.EMBEDDING_TOKENS]: 'Embedding tokens',
}

/** Aggregation bucket for the usage breakdown. */
export const usageGroupByEnum = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
} as const

export type UsageGroupByValue = (typeof usageGroupByEnum)[keyof typeof usageGroupByEnum]

export const topUpShape = {
  amount: z.string(),
  date: z.string(),
  stripeSessionId: z.string(),
  status: z.string(),
}

export const walletShape = {
  balance: z.string(),
  currency: z.string(),
  // Notify/warn once the balance drops to or below this amount. "0" disables it.
  lowBalanceThreshold: z.string(),
  recentTopups: z.array(z.object(topUpShape)),
}

/** One of the caller's orgs with its wallet balance — for org pickers that must
 * bill a specific org (e.g. receipt AI generation). */
export const orgWalletSummaryShape = {
  organizationId: z.string(),
  organizationName: z.string(),
  balance: z.string(),
  currency: z.string(),
  hasCredits: z.boolean(),
  aiReceiptScanningEnabled: z.boolean(),
  aiStatementReconciliationEnabled: z.boolean(),
}

export const usageRecordShape = {
  period: z.string(),
  eventType: z.enum(usageEventTypeEnum),
  totalUnits: z.number(),
  totalCost: z.string(),
}

/** One row of the public pricing table: the active rate for an event type. */
export const rateCardShape = {
  eventType: z.enum(usageEventTypeEnum),
  eventLabel: z.string(),
  costPerUnit: z.string(),
  unitLabel: z.string(),
  effectiveFrom: z.string(),
}

/** True when the wallet balance has dropped to or below its low-balance
 * threshold — the cue to warn (non-blocking) before sends start failing. A
 * threshold of "0" disables the warning, mirroring the backend. */
export function isWalletBalanceLow(
  wallet: Pick<Wallet, 'balance' | 'lowBalanceThreshold'> | null | undefined,
): boolean {
  if (!wallet) return false
  const threshold = Number(wallet.lowBalanceThreshold)
  const balance = Number(wallet.balance)
  if (!Number.isFinite(threshold) || !Number.isFinite(balance) || threshold <= 0) return false
  return balance <= threshold
}

export type TopUp = GetInferredFromRaw<typeof topUpShape>
export type Wallet = GetInferredFromRaw<typeof walletShape>
export type OrgWalletSummary = GetInferredFromRaw<typeof orgWalletSummaryShape>
export type UsageRecord = GetInferredFromRaw<typeof usageRecordShape>
export type RateCard = GetInferredFromRaw<typeof rateCardShape>
